import { useQuery } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../../api/client';

export type Location = Schemas['Location'];
export type StockBalance = Schemas['StockBalance'];
export type StockMovement = Schemas['StockMovement'];
export type StockAdjustment = Schemas['StockAdjustment'];
export type StockCount = Schemas['StockCount'];
export type StockCountSummary = Schemas['StockCountList']['data'][number];

/** Query keys for this feature: invalidate `inventoryKeys.all` after any stock mutation. */
export const inventoryKeys = {
  all: ['inventory'] as const,
  locations: ['inventory', 'locations'] as const,
  balances: (q: object) => ['inventory', 'balances', q] as const,
  valuation: ['inventory', 'valuation'] as const,
  movements: (q: object) => ['inventory', 'movements', q] as const,
  adjustments: (q: object) => ['inventory', 'adjustments', q] as const,
  adjustment: (id: string) => ['inventory', 'adjustment', id] as const,
  counts: (q: object) => ['inventory', 'counts', q] as const,
  count: (id: string) => ['inventory', 'count', id] as const,
};

export function useLocations() {
  return useQuery({
    queryKey: inventoryKeys.locations,
    queryFn: () => unwrap(api.GET('/locations')),
    staleTime: 60_000,
    select: (d) => d.data,
  });
}

/** The shop's calendar date ("YYYY-MM-DD") in its time zone. */
export function todayIn(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export const MOVEMENT_TYPES = [
  'OPENING',
  'RECEIPT',
  'DELIVERY',
  'SALES_RETURN',
  'PURCHASE_RETURN',
  'ADJUSTMENT',
  'WRITE_OFF',
  'TRANSFER_IN',
  'TRANSFER_OUT',
  'COUNT_ADJUSTMENT',
  'PRODUCTION_CONSUME',
  'PRODUCTION_OUTPUT',
] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];

export const MOVEMENT_LABELS: Record<string, string> = {
  OPENING: 'Opening stock',
  RECEIPT: 'Goods received',
  DELIVERY: 'Delivered',
  SALES_RETURN: 'Sales return',
  PURCHASE_RETURN: 'Purchase return',
  ADJUSTMENT: 'Adjustment',
  WRITE_OFF: 'Write-off',
  TRANSFER_IN: 'Transfer in',
  TRANSFER_OUT: 'Transfer out',
  COUNT_ADJUSTMENT: 'Count difference',
  PRODUCTION_CONSUME: 'Used in production',
  PRODUCTION_OUTPUT: 'Produced',
};

export const ADJUSTMENT_KIND_LABELS: Record<StockAdjustment['kind'], string> = {
  OPENING: 'Opening stock',
  ADJUSTMENT: 'Adjustment',
  WRITE_OFF: 'Write-off',
  COUNT: 'Stock count',
};

export const LOCATION_KIND_LABELS: Record<Location['kind'], string> = {
  STORE: 'Store',
  DAMAGED: 'Damaged goods',
  TRANSIT: 'In transit',
};

/**
 * Header params for POSTs: the generated types require `idempotency-key`. Pass a stable key to make a
 * retried submit safe (the API replays the first response); without one a fresh key is generated.
 */
export function idem(key: string = crypto.randomUUID()) {
  return { 'idempotency-key': key };
}
